import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
} from '@mui/material';
import { AuthContext } from '../../../context/auth-context';
import ProductionMetricsAPI from '../../../../services/api/analytics/production-metrics-api';
import { getProjectDateRangeErrorMessage } from '../../../../utils/projectDateRangeError';
import WeekNavigator from '../components/WeekNavigator';
import LaborGauge from '../components/LaborGauge';
import MetricSideTable from '../components/MetricSideTable';
import LaborHoursTable from '../components/LaborHoursTable';

import FilterListIcon from '@mui/icons-material/FilterList';

const Card = ({ children, sx = {} }) => (
  <Box
    sx={{
      bgcolor: 'background.paper',
      border: '0.5px solid',
      borderColor: 'divider',
      borderRadius: 2,
      p: 2,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SecLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: '10px',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: 'text.secondary',
      mb: 0.5,
    }}
  >
    {children}
  </Typography>
);

const SubLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: '12px',
      color: 'text.secondary',
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
);

// goodDirection comes from the API: 'below' = Actual ÷ Earned (under 1.0 is
// good), 'above' = Earned ÷ Actual (over 1.0 is good).

/**
 * Mirrors ProductionMetricsService.applyKpiOrientation for the filtered
 * re-aggregation, so filtered values use the same formulas as the API.
 */
const applyKpiOrientation = (actual, earned, planned, goodDirection) =>
  goodDirection === 'above'
    ? {
        lpf: actual > 0 ? earned / actual : null,
        spi: planned > 0 ? earned / planned : null,
      }
    : {
        lpf: earned > 0 ? actual / earned : null,
        spi: earned > 0 ? planned / earned : null,
      };

const isGoodValue = (value, goodDirection) =>
  value != null && (goodDirection === 'above' ? value >= 1.0 : value <= 1.0);

const GOOD_COLOR = '#0F6E56';
const BAD_COLOR = '#993C1D';

const GaugeDirectionLabels = ({ goodDirection }) => {
  const aboveIsGood = goodDirection === 'above';
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        mt: -0.5,
        px: 1,
      }}
    >
      <Typography
        sx={{ fontSize: '10px', color: aboveIsGood ? BAD_COLOR : GOOD_COLOR }}
      >
        {aboveIsGood ? '◀ Under' : '◀ Good'}
      </Typography>
      <Typography
        sx={{ fontSize: '10px', color: aboveIsGood ? GOOD_COLOR : BAD_COLOR }}
      >
        {aboveIsGood ? 'Good ▶' : 'Over ▶'}
      </Typography>
    </Box>
  );
};

/**
 * Returns the ISO date string of the current week-ending Sunday.
 * If today is Sunday, returns today; otherwise returns upcoming Sunday.
 */
function getCurrentSunday() {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayOfWeek = today.getDay();
  const daysToAdd = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
  today.setDate(today.getDate() + daysToAdd);
  return today.toISOString().slice(0, 10);
}

/**
 * Returns the ISO date string of the most recent Sunday strictly before today.
 * If today is Sunday, returns the Sunday from 7 days ago.
 */
function getPreviousSunday() {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayOfWeek = today.getDay();
  const daysBack = dayOfWeek === 0 ? 7 : dayOfWeek;
  today.setDate(today.getDate() - daysBack);
  return today.toISOString().slice(0, 10);
}

function shiftWeek(isoDate, delta) {
  const d = new Date(isoDate + 'T00:00:00');
  d.setDate(d.getDate() + delta * 7);
  return d.toISOString().slice(0, 10);
}

function SnapshotRegenDialog({
  open,
  onClose,
  onConfirm,
  snapshotCountLoading,
  snapshotRowCount,
  regenerating,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Regenerate all snapshots
      </DialogTitle>
      <DialogContent>
        <DialogContentText component="div" sx={{ color: 'text.primary' }}>
          <Typography sx={{ mb: 1.5, fontWeight: 600, color: '#b45309' }}>
            This will permanently delete all snapshot rows for this project and
            rebuild them from scratch.
          </Typography>
          <Typography component="p" sx={{ mb: 1 }}>
            {snapshotCountLoading && 'Loading current row count…'}
            {!snapshotCountLoading && snapshotRowCount != null && (
              <>
                There are currently{' '}
                <strong>{snapshotRowCount.toLocaleString('en-US')}</strong>{' '}
                snapshot row{snapshotRowCount === 1 ? '' : 's'} for this
                project.
              </>
            )}
            {!snapshotCountLoading && snapshotRowCount == null && (
              <>
                Could not load row count. Regeneration will still delete all
                rows for this project.
              </>
            )}
          </Typography>
          <Typography component="p" sx={{ mb: 1, fontSize: '13px' }}>
            Use when the schedule or budget changed and prior weeks need to
            match new planned hours. This action is logged and cannot be undone
            from the app.
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={regenerating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={onConfirm}
          disabled={regenerating}
          sx={{ fontWeight: 700 }}
        >
          Regenerate — I understand
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * KPI Tab — Phase 5.
 *
 * Layout:
 *   1. WeekNavigator at top
 *   2. Two gauge cards side by side (LPF left, SPI right)
 *   3. LaborHoursTable below
 */
const KpiTab = ({
  projectId,
  filterAreaName,
  filterPmId,
  filterSearchText,
}) => {
  const auth = useContext(AuthContext);
  const isSuperAdmin = !!auth.isSuperAdmin;

  const [currentSunday] = useState(() => getCurrentSunday());
  const [weekEnding, setWeekEnding] = useState(() => getPreviousSunday());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [regenDialogOpen, setRegenDialogOpen] = useState(false);
  const [snapshotRowCount, setSnapshotRowCount] = useState(null);
  const [snapshotCountLoading, setSnapshotCountLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenBanner, setRegenBanner] = useState(null);

  const isCurrentWeek = weekEnding === currentSunday;

  // Client-side packages filtering & KPI metric recalculation
  const filteredData = React.useMemo(() => {
    if (!data) return null;

    const hasFilter =
      Boolean(filterAreaName) ||
      Boolean(filterPmId) ||
      Boolean(filterSearchText?.trim());

    if (!hasFilter) {
      return data;
    }

    const filteredSearchLower = filterSearchText.trim().toLowerCase();

    const matchesFilter = (pkg) => {
      // Area filter
      if (filterAreaName) {
        if (
          !pkg.areaName ||
          pkg.areaName.toLowerCase() !== filterAreaName.toLowerCase()
        ) {
          return false;
        }
      }
      // PM filter
      if (filterPmId) {
        if (pkg.pmEmployeeId !== filterPmId) {
          return false;
        }
      }
      // Search text contains
      if (filteredSearchLower) {
        const combined =
          `${pkg.autodeskName || ''} ${pkg.packageName || ''}`.toLowerCase();
        if (!combined.includes(filteredSearchLower)) {
          return false;
        }
      }
      return true;
    };

    // Filter package breakdown drilldowns
    const pd = data.packageDrilldowns || {};
    const filteredDrilldowns = {
      projectTotal: (pd.projectTotal || []).filter(matchesFilter),
      projectToDate: (pd.projectToDate || []).filter(matchesFilter),
      thisWeek: (pd.thisWeek || []).filter(matchesFilter),
      lastWeek: (pd.lastWeek || []).filter(matchesFilter),
      generalConditions: {
        projectToDate: (pd.generalConditions?.projectToDate || []).filter(
          matchesFilter
        ),
        thisWeek: (pd.generalConditions?.thisWeek || []).filter(matchesFilter),
      },
      changeOrders: {
        projectToDate: (pd.changeOrders?.projectToDate || []).filter(
          matchesFilter
        ),
        thisWeek: (pd.changeOrders?.thisWeek || []).filter(matchesFilter),
      },
    };

    // Re-aggregate work package hours
    const wpProjectToDate = {
      totalPlanned: filteredDrilldowns.projectToDate.reduce(
        (sum, p) => sum + (p.plannedHours || 0),
        0
      ),
      totalEarned: filteredDrilldowns.projectToDate.reduce(
        (sum, p) => sum + (p.earnedHours || 0),
        0
      ),
      totalActual: filteredDrilldowns.projectToDate.reduce(
        (sum, p) => sum + (p.actualHours || 0),
        0
      ),
    };
    const wpThisWeek = {
      totalPlanned: filteredDrilldowns.thisWeek.reduce(
        (sum, p) => sum + (p.plannedHours || 0),
        0
      ),
      totalEarned: filteredDrilldowns.thisWeek.reduce(
        (sum, p) => sum + (p.earnedHours || 0),
        0
      ),
      totalActual: filteredDrilldowns.thisWeek.reduce(
        (sum, p) => sum + (p.actualHours || 0),
        0
      ),
    };
    const wpLastWeek = {
      totalPlanned: filteredDrilldowns.lastWeek.reduce(
        (sum, p) => sum + (p.plannedHours || 0),
        0
      ),
      totalEarned: filteredDrilldowns.lastWeek.reduce(
        (sum, p) => sum + (p.earnedHours || 0),
        0
      ),
      totalActual: filteredDrilldowns.lastWeek.reduce(
        (sum, p) => sum + (p.actualHours || 0),
        0
      ),
    };

    // Re-aggregate GC
    const gcProjectToDate = {
      totalActual: filteredDrilldowns.generalConditions.projectToDate.reduce(
        (sum, p) => sum + (p.actualHours || 0),
        0
      ),
    };
    const gcThisWeek = {
      totalActual: filteredDrilldowns.generalConditions.thisWeek.reduce(
        (sum, p) => sum + (p.actualHours || 0),
        0
      ),
    };

    // Re-aggregate CO
    const coProjectToDate = {
      totalActual: filteredDrilldowns.changeOrders.projectToDate.reduce(
        (sum, p) => sum + (p.actualHours || 0),
        0
      ),
    };
    const coThisWeek = {
      totalActual: filteredDrilldowns.changeOrders.thisWeek.reduce(
        (sum, p) => sum + (p.actualHours || 0),
        0
      ),
    };

    // Re-aggregate budgets
    const workPackageHrs = filteredDrilldowns.projectTotal.reduce(
      (sum, p) => sum + (p.budgetHours || 0),
      0
    );
    const generalConditionsHrs =
      filteredDrilldowns.generalConditions.projectToDate.reduce(
        (sum, p) => sum + (p.budgetHours || 0),
        0
      );
    const changeOrderHrs = filteredDrilldowns.changeOrders.projectToDate.reduce(
      (sum, p) => sum + (p.budgetHours || 0),
      0
    );

    const newBudgetTotal = {
      workPackageHrs,
      generalConditionsHrs,
      changeOrderHrs,
      totalHrs: workPackageHrs + generalConditionsHrs + changeOrderHrs,
    };

    // LPF and SPI recalculations
    const overallActual = wpProjectToDate.totalActual;
    const overallEarned = wpProjectToDate.totalEarned;
    const overallPlanned = wpProjectToDate.totalPlanned;

    const thisWeekActual = wpThisWeek.totalActual;
    const thisWeekEarned = wpThisWeek.totalEarned;
    const thisWeekPlanned = wpThisWeek.totalPlanned;

    const lastWeekActual = wpLastWeek.totalActual;
    const lastWeekEarned = wpLastWeek.totalEarned;
    const lastWeekPlanned = wpLastWeek.totalPlanned;

    // Same orientation as the unfiltered API response (ticket #264).
    const goodDirection = data.metrics?.goodDirection ?? 'below';

    const { lpf: lpfOverall, spi: spiOverall } = applyKpiOrientation(
      overallActual,
      overallEarned,
      overallPlanned,
      goodDirection
    );

    const { lpf: lpfThisWeek, spi: spiThisWeek } = applyKpiOrientation(
      thisWeekActual,
      thisWeekEarned,
      thisWeekPlanned,
      goodDirection
    );

    const { lpf: lpfLastWeek, spi: spiLastWeek } = applyKpiOrientation(
      lastWeekActual,
      lastWeekEarned,
      lastWeekPlanned,
      goodDirection
    );

    const lpfTrend =
      lpfThisWeek != null && lpfLastWeek != null
        ? lpfThisWeek < lpfLastWeek
          ? 'down'
          : lpfThisWeek > lpfLastWeek
            ? 'up'
            : 'flat'
        : 'flat';

    const spiTrend =
      spiThisWeek != null && spiLastWeek != null
        ? spiThisWeek < spiLastWeek
          ? 'down'
          : spiThisWeek > spiLastWeek
            ? 'up'
            : 'flat'
        : 'flat';

    const newMetrics = {
      lpfOverall,
      lpfThisWeek,
      lpfLastWeek,
      lpfTrend,
      spiOverall,
      spiThisWeek,
      spiLastWeek,
      spiTrend,
      costVarianceOverall: overallActual - overallEarned,
      schedVarianceOverall: overallEarned - overallPlanned,
      costVarianceThisWeek: thisWeekActual - thisWeekEarned,
      schedVarianceThisWeek: thisWeekEarned - thisWeekPlanned,
      goodDirection,
    };

    return {
      ...data,
      budgetTotal: newBudgetTotal,
      workPackages: {
        projectToDate: wpProjectToDate,
        thisWeek: wpThisWeek,
        lastWeek: wpLastWeek,
      },
      generalConditions: {
        projectToDate: gcProjectToDate,
        thisWeek: gcThisWeek,
      },
      changeOrders: {
        projectToDate: coProjectToDate,
        thisWeek: coThisWeek,
      },
      metrics: newMetrics,
      packageDrilldowns: filteredDrilldowns,
    };
  }, [data, filterAreaName, filterPmId, filterSearchText]);

  const fetchSummary = useCallback(
    async (we) => {
      if (!projectId) return;
      setLoading(true);
      setError(null);
      try {
        const result = await ProductionMetricsAPI.getKpiSummary(
          projectId,
          we,
          auth.token
        );
        setData(result);
      } catch (err) {
        setError(err.message || 'Failed to load KPI data');
      } finally {
        setLoading(false);
      }
    },
    [projectId, auth.token]
  );

  useEffect(() => {
    setWeekEnding(getPreviousSunday());
  }, [projectId]);

  useEffect(() => {
    fetchSummary(weekEnding);
  }, [weekEnding, fetchSummary]);

  const handlePrev = () => setWeekEnding((we) => shiftWeek(we, -1));
  const handleNext = () => {
    if (!isCurrentWeek) setWeekEnding((we) => shiftWeek(we, 1));
  };
  const handleCurrent = () => setWeekEnding(currentSunday);

  useEffect(() => {
    setRegenBanner(null);
  }, [projectId]);

  useEffect(() => {
    if (!regenDialogOpen || !projectId || !auth.token) return undefined;

    let cancelled = false;
    setSnapshotCountLoading(true);
    setSnapshotRowCount(null);

    (async () => {
      try {
        const res = await ProductionMetricsAPI.getSnapshotRowCount(
          projectId,
          auth.token
        );
        if (!cancelled) setSnapshotRowCount(res.rowCount ?? 0);
      } catch {
        if (!cancelled) setSnapshotRowCount(null);
      } finally {
        if (!cancelled) setSnapshotCountLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [regenDialogOpen, projectId, auth.token]);

  const handleOpenRegenDialog = () => setRegenDialogOpen(true);
  const handleCloseRegenDialog = () => {
    if (!regenerating) setRegenDialogOpen(false);
  };

  const handleConfirmRegenerate = async () => {
    if (!projectId || !auth.token) return;
    setRegenerating(true);
    setRegenBanner(null);
    try {
      const result = await ProductionMetricsAPI.regenerateSnapshots(
        projectId,
        auth.token
      );
      const secs = ((result.durationMs || 0) / 1000).toFixed(1);
      const errPart =
        (result.errorCount || 0) > 0
          ? ` ${result.errorCount} error(s) — check server logs.`
          : '';
      setRegenBanner({
        severity: (result.errorCount || 0) > 0 ? 'warning' : 'success',
        message: `Snapshots regenerated — ${result.rowsCreated ?? 0} created, ${result.rowsSkipped ?? 0} skipped, ${result.rowsDeleted ?? 0} deleted previously. (${secs}s)${errPart}`,
      });
      setRegenDialogOpen(false);
      await fetchSummary(weekEnding);
    } catch (e) {
      const msg = getProjectDateRangeErrorMessage(
        e,
        'Regeneration failed. Check server logs.'
      );
      setRegenBanner({ severity: 'error', message: msg });
    } finally {
      setRegenerating(false);
    }
  };

  const superAdminToolbar = isSuperAdmin && projectId && (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 1,
        mb: 1.5,
      }}
    >
      {regenerating ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} />
          <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
            Regenerating snapshots…
          </Typography>
        </Box>
      ) : (
        <Button
          variant="outlined"
          color="warning"
          size="small"
          onClick={handleOpenRegenDialog}
          disabled={!auth.token}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#d97706',
            color: '#b45309',
            '&:hover': {
              borderColor: '#b45309',
              bgcolor: 'rgba(217, 119, 6, 0.06)',
            },
          }}
        >
          Regenerate all snapshots
        </Button>
      )}
    </Box>
  );

  const snapshotRegenDialog = isSuperAdmin && projectId && (
    <SnapshotRegenDialog
      open={regenDialogOpen}
      onClose={handleCloseRegenDialog}
      onConfirm={handleConfirmRegenerate}
      snapshotCountLoading={snapshotCountLoading}
      snapshotRowCount={snapshotRowCount}
      regenerating={regenerating}
    />
  );

  // ── Guards ────────────────────────────────────────────────────────────

  if (!projectId) {
    return (
      <Typography sx={{ color: 'text.secondary', mt: 4, textAlign: 'center' }}>
        Select a project to view KPI data.
      </Typography>
    );
  }

  if (loading && !data) {
    return (
      <>
        {snapshotRegenDialog}
        <Box sx={{ mt: 1 }}>
          {superAdminToolbar}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress size={32} />
          </Box>
        </Box>
      </>
    );
  }

  if (error && !data) {
    return (
      <>
        {snapshotRegenDialog}
        <Box sx={{ mt: 1 }}>
          {superAdminToolbar}
          <Typography color="error" sx={{ mt: 4, textAlign: 'center' }}>
            {error}
          </Typography>
        </Box>
      </>
    );
  }

  if (!filteredData) {
    return <>{snapshotRegenDialog}</>;
  }

  const {
    metrics,
    budgetTotal,
    workPackages,
    generalConditions,
    changeOrders,
    projectToDate: legacyProjectToDate,
    thisWeek: legacyThisWeek,
    lastWeek: legacyLastWeek,
    packageDrilldowns,
  } = filteredData;

  const projectToDate = workPackages?.projectToDate || legacyProjectToDate;
  const thisWeek = workPackages?.thisWeek || legacyThisWeek;
  const lastWeek = workPackages?.lastWeek || legacyLastWeek;

  const goodDirection = metrics?.goodDirection ?? 'below';
  const aboveIsGood = goodDirection === 'above';

  const lpfCheck = isGoodValue(metrics?.lpfOverall, goodDirection) ? '✓' : '';
  const spiCheck = isGoodValue(metrics?.spiOverall, goodDirection) ? '✓' : '';

  const lpfCaption = aboveIsGood
    ? 'Earned ÷ Actual · over 1.000 = under budget'
    : 'Actual ÷ Earned · under 1.000 = under budget';
  const spiCaption = aboveIsGood
    ? 'Earned ÷ Planned · over 1.000 = ahead of schedule'
    : 'Planned ÷ Earned · under 1.000 = ahead of schedule';

  const isFiltered =
    Boolean(filterAreaName) ||
    Boolean(filterPmId) ||
    Boolean(filterSearchText?.trim());

  return (
    <>
      {snapshotRegenDialog}
      <Box sx={{ opacity: loading ? 0.55 : 1, transition: 'opacity 0.2s' }}>
        {regenBanner && (
          <Alert
            severity={regenBanner.severity}
            onClose={() => setRegenBanner(null)}
            sx={{ mb: 1.5 }}
          >
            {regenBanner.message}
          </Alert>
        )}

        {isFiltered && (
          <Alert
            severity="info"
            icon={<FilterListIcon />}
            sx={{
              mb: 2,
              borderRadius: '8px',
              border: '1.5px solid #bfdbfe',
              bgcolor: '#eff6ff',
              color: '#1e3a8a',
              '& .MuiAlert-icon': {
                color: '#2563eb',
              },
            }}
          >
            Filters active — KPI values reflect filtered packages only, not the
            full project.
          </Alert>
        )}

        {superAdminToolbar}

        {/* ── Week Navigator ─────────────────────────────────────────────── */}
        <WeekNavigator
          weekEnding={weekEnding}
          isCurrentWeek={isCurrentWeek}
          onPrev={handlePrev}
          onNext={handleNext}
          onCurrent={handleCurrent}
        />

        {/* ── Gauge Cards ────────────────────────────────────────────────── */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              lg: 'repeat(2, minmax(360px, 420px))',
            },
            justifyContent: 'center',
            gap: { xs: 4, lg: 14 },
            mb: 1.5,
          }}
        >
          {/* LPF Card */}
          <Card>
            <SecLabel>Labor Productivity Factor</SecLabel>
            <SubLabel>
              {lpfCaption} {lpfCheck}
            </SubLabel>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{ flexShrink: 0 }}>
                <LaborGauge
                  value={metrics?.lpfOverall ?? 1.0}
                  size={138}
                  goodDirection={goodDirection}
                />
                <GaugeDirectionLabels goodDirection={goodDirection} />
              </Box>

              <MetricSideTable
                overall={metrics?.lpfOverall}
                thisWeek={metrics?.lpfThisWeek}
                lastWeek={metrics?.lpfLastWeek}
                trend={metrics?.lpfTrend}
                goodDirection={goodDirection}
              />
            </Box>
          </Card>

          {/* SPI Card */}
          <Card>
            <SecLabel>Schedule Performance Index</SecLabel>
            <SubLabel>
              {spiCaption} {spiCheck}
            </SubLabel>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{ flexShrink: 0 }}>
                <LaborGauge
                  value={metrics?.spiOverall ?? 1.0}
                  size={138}
                  goodDirection={goodDirection}
                />
                <GaugeDirectionLabels goodDirection={goodDirection} />
              </Box>

              <MetricSideTable
                overall={metrics?.spiOverall}
                thisWeek={metrics?.spiThisWeek}
                lastWeek={metrics?.spiLastWeek}
                trend={metrics?.spiTrend}
                goodDirection={goodDirection}
              />
            </Box>
          </Card>
        </Box>

        {/* ── Labor Hours Table ──────────────────────────────────────────── */}
        <LaborHoursTable
          budgetTotal={budgetTotal}
          workPackages={workPackages}
          generalConditions={generalConditions}
          changeOrders={changeOrders}
          projectToDate={projectToDate}
          thisWeek={thisWeek}
          lastWeek={lastWeek}
          metrics={metrics}
          packageDrilldowns={packageDrilldowns}
        />
      </Box>
    </>
  );
};

export default KpiTab;
